import React, { useEffect, useState } from 'react';
import { Button, Modal, Message, Table } from 'semantic-ui-react';
import { useComponentManager } from '../../providers/ComponentManagerProvider';

const impactRows = impact => [
    { key: 'childComponents', label: 'Child components', value: impact.childComponents },
    { key: 'points', label: 'Points', value: impact.points },
    { key: 'functions', label: 'Functions', value: impact.functions },
    { key: 'paragraphs', label: 'Paragraphs', value: impact.paragraphs },
    { key: 'tasks', label: 'Tasks', value: impact.tasks },
    { key: 'issues', label: 'Issues', value: impact.issues },
    { key: 'insights', label: 'Insights', value: impact.insights },
    {
        key: 'mapping',
        label: 'Mapping tree entries',
        value: (impact.mappingRoots ?? 0) + (impact.mappingRelationships ?? 0),
    },
    {
        key: 'schedule',
        label: 'Schedule tree entries',
        value: (impact.scheduleRoots ?? 0) + (impact.scheduleRelationships ?? 0),
    },
    { key: 'equipment', label: 'Equipment', value: impact.equipment },
];

export const ComponentDeleteConfirmation = ({ open, setOpen, component, impact, impactError, onDeleted }) => {
    const { deleteComponent } = useComponentManager();

    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const componentId = component?.id;

    useEffect(() => {
        if (open) setDeleteError(null);
    }, [open, componentId]);

    const blocked = !!impact && impact.equipment > 0;
    const hasRelatedItems = !!impact && impactRows(impact).some(row => (row.value ?? 0) > 0);
    const confirmDisabled = impactError || !impact || blocked || isDeleting;

    const handleConfirm = () => {
        setIsDeleting(true);
        setDeleteError(null);
        deleteComponent(componentId)
            .then(res => {
                setIsDeleting(false);
                setOpen(false);
                onDeleted(res?.data);
            })
            .catch(err => {
                setIsDeleting(false);
                const status = err?.response?.status;
                const serverMessage =
                    err?.response?.data?.message || err?.response?.data;
                if (status === 409) {
                    setDeleteError(
                        typeof serverMessage === 'string' && serverMessage
                            ? serverMessage
                            : 'This component can no longer be deleted because equipment now references it.'
                    );
                } else if (status === 404) {
                    setDeleteError('This component no longer exists. It may have already been deleted.');
                } else {
                    setDeleteError(
                        typeof serverMessage === 'string' && serverMessage
                            ? serverMessage
                            : 'Failed to delete component. Please try again.'
                    );
                }
            });
    };

    return (
        <Modal open={open} onClose={() => !isDeleting && setOpen(false)} size="small">
            <Modal.Header>Delete component</Modal.Header>
            <Modal.Content>
                <p data-testid="delete-target">
                    You are about to permanently delete{' '}
                    "<strong>{component ? `${component.num ?? ''} ${component.name ?? component.description ?? ''}`.trim() : ''}</strong>"
                    {hasRelatedItems ? ' and everything in its subtree' : ''}. This cannot be undone.
                </p>

                {impactError && (
                    <Message negative role="alert" data-testid="delete-impact-error">
                        <Message.Header>Couldn't load the delete impact</Message.Header>
                        <p>Please close this dialog and try again.</p>
                    </Message>
                )}

                {impact && !impactError && (
                    <>
                        {hasRelatedItems && (
                            <>
                            <div style={{ fontWeight: 700, color: '#6b7c85', marginBottom: '0.25rem' }}>
                                This will also delete:
                            </div>
                            <Table
                                basic
                                compact
                                aria-label="Items that will be deleted"
                                data-testid="delete-impact-table"
                            >
                                <Table.Body>
                                    {impactRows(impact)
                                        .filter(row => (row.value ?? 0) > 0)
                                        .map(row => (
                                            <Table.Row key={row.key}>
                                                <Table.Cell>{row.label}</Table.Cell>
                                                <Table.Cell
                                                    textAlign="right"
                                                    data-testid={`delete-impact-${row.key}`}
                                                >
                                                    {row.value}
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                </Table.Body>
                            </Table>
                            </>
                        )}

                        {blocked && (
                            <Message negative role="alert" data-testid="delete-blocked">
                                <Message.Header>This component can't be deleted</Message.Header>
                                <p>
                                    {impact.equipment} piece{impact.equipment === 1 ? '' : 's'} of
                                    equipment {impact.equipment === 1 ? 'is' : 'are'} built from this
                                    component or its subtree. Remove or reassign that equipment
                                    before deleting.
                                </p>
                            </Message>
                        )}
                    </>
                )}

                {deleteError && (
                    <Message negative role="alert" data-testid="delete-error">
                        {deleteError}
                    </Message>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    onClick={() => setOpen(false)}
                    disabled={isDeleting}
                    data-testid="delete-cancel"
                >
                    Cancel
                </Button>
                <Button
                    negative
                    onClick={handleConfirm}
                    loading={isDeleting}
                    disabled={confirmDisabled}
                    data-testid="delete-confirm"
                >
                    Delete
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
